import React from 'react';

import {
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  InputGroup,
  InputLeftAddon,
  Badge,
  Divider,
} from '@chakra-ui/react';

import { StarIcon } from '@chakra-ui/icons';

import { useColors } from './colors';

const SupplierSettings = ({ listing }) => {
  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    textColor,
    warningColor,
    hoverBg: readOnlyBg,
    inputColor: readOnlyColor,
  } = useColors();

  const alertColor = warningColor;

  const currentSupplier = React.useMemo(() => {
    if (!listing?.supplier?.supplierId || !listing?.store?.suppliers) {
      return null;
    }

    return listing.store.suppliers.find(
      (supplier) => supplier.supplierId === listing.supplier.supplierId,
    );
  }, [listing]);

  const currentPriceRange = React.useMemo(() => {
    if (!currentSupplier?.priceRangeQuantities || !listing?.details?.price) {
      return null;
    }

    const currentPrice = listing.details.price;
    return currentSupplier.priceRangeQuantities.find(
      (range) => currentPrice >= range.minPrice && currentPrice <= range.maxPrice,
    );
  }, [currentSupplier, listing]);

  if (!currentSupplier) {
    return (
      <Box
        bg={bgColor}
        borderRadius="xl"
        p={6}
        border="1px solid"
        borderColor={borderColor}
        flex="1"
        minH="100%"
      >
        <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
          Supplier Settings
        </Text>
        <Text color={labelColor} fontSize="sm" fontStyle="italic">
          No supplier information available
        </Text>
      </Box>
    );
  }

  const supplierSettings = {
    markupPercentage: (currentSupplier.markupPercentage * 100).toFixed(1),
    priceOffsetTolerance: (currentSupplier.priceTolerancePercentage * 100).toFixed(1),
    stockAvailability: currentSupplier.stockAvailabilityFallback ? 'active' : 'inactive',
    priority: currentSupplier.priorityRank.toString(),
    quantityOffset: currentSupplier.quantityOffset.toString(),
    defaultMaxQuantityEnabled: currentSupplier.defaultQuantityMaxLimitEnabled,
    defaultMaxQuantity: currentSupplier.quantityMaxLimit.toString(),
  };

  const StatusBadge = ({ status }) => (
    <Badge
      colorScheme={status === 'active' ? 'green' : 'gray'}
      variant="solid"
      px={6}
      py={1}
      borderRadius="full"
      textTransform="capitalize"
      fontSize="lg"
    >
      {status}
    </Badge>
  );

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      flex="1"
      minH="100%"
    >
      <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
        Supplier Settings
      </Text>

      <VStack spacing={5} align="stretch">
        {/* First Row: Markup % and Price Offset Tolerance */}
        <HStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Mark Up %
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="%" />
              <Input
                value={supplierSettings.markupPercentage}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Price Offset Tolerance
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="%" />
              <Input
                value={supplierSettings.priceOffsetTolerance}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>
        </HStack>

        {/* Second Row: Stock Availability and Priority */}
        <HStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Stock Availability
            </FormLabel>
            <Box pt={2}>
              <StatusBadge status={supplierSettings.stockAvailability} />
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Priority
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children={<StarIcon boxSize={3} />} />
              <Input
                value={supplierSettings.priority}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>
        </HStack>

        {/* Third Row: Quantity Offset and Default Max Quantity */}
        <HStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Quantity Offset
            </FormLabel>
            <InputGroup>
              <InputLeftAddon children="#" />
              <Input
                value={supplierSettings.quantityOffset}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                size="md"
                borderRadius="md"
              />
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
              Default Max Quantity Limit
            </FormLabel>
            {supplierSettings.defaultMaxQuantityEnabled ? (
              <InputGroup>
                <InputLeftAddon children="#" />
                <Input
                  value={supplierSettings.defaultMaxQuantity}
                  isReadOnly
                  bg={readOnlyBg}
                  color={readOnlyColor}
                  size="md"
                  borderRadius="md"
                />
              </InputGroup>
            ) : (
              <Box pt={2}>
                <Text color={labelColor} fontSize="sm" fontStyle="italic">
                  Disabled
                </Text>
              </Box>
            )}
          </FormControl>
        </HStack>

        {/* Selected Price-Range Max Quantity Limit Section - Only show if there's a matching price range */}
        {currentPriceRange && (
          <>
            <Divider />
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color={headingColor} mb={4}>
                Selected Price-Range Max Quantity Limit
              </Text>

              <HStack spacing={5} align="stretch">
                <FormControl>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color={labelColor}
                    mb={2}
                  >
                    Min Price
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon children="$" />
                    <Input
                      value={currentPriceRange.minPrice.toFixed(2)}
                      isReadOnly
                      bg={readOnlyBg}
                      color={readOnlyColor}
                      size="md"
                      borderRadius="md"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color={labelColor}
                    mb={2}
                  >
                    Max Price
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon children="$" />
                    <Input
                      value={currentPriceRange.maxPrice.toFixed(2)}
                      isReadOnly
                      bg={readOnlyBg}
                      color={readOnlyColor}
                      size="md"
                      borderRadius="md"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="semibold"
                    color={labelColor}
                    mb={2}
                  >
                    Allowed Quantity
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon children="#" />
                    <Input
                      value={currentPriceRange.allowedQuantity.toString()}
                      isReadOnly
                      bg={readOnlyBg}
                      color={readOnlyColor}
                      size="md"
                      borderRadius="md"
                    />
                  </InputGroup>
                </FormControl>
              </HStack>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default SupplierSettings;
